import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check } from "lucide-react";

const TYPE_TAB = {
  "weekly-schedule": "schedule",
  "auto-plan": "schedule",
  "general-exams": "general",
  "branch-exams": "branch",
  "study-cards": "weak",
  "tarama-exams": "resources",
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      /* sessiz */
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const openNotif = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch (e) {
        /* sessiz */
      }
    }
    const tab = TYPE_TAB[n.type] || "overview";
    window.dispatchEvent(new CustomEvent("ta-navigate", { detail: { tab, studentId: n.student_id } }));
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((p) => p.map((i) => ({ ...i, read: true })));
      toast.success("Tümü okundu");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
        data-testid="notif-bell"
      >
        <Bell className="text-slate-600" size={19} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] card-ta shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <span className="font-display font-semibold text-sm text-slate-900">Bildirimler</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="notif-mark-all">
                <Check size={13} /> Tümünü okundu yap
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500 text-sm">Bildirim yok.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotif(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-200 text-sm transition-colors hover:bg-indigo-50 ${n.read ? "text-slate-500" : "text-slate-800 bg-slate-50"}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />}
                    <div className="flex-1">
                      <div>{n.message}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")} · Görüntülemek için tıkla</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
